import {gql} from "@apollo/client";
import {useLazyQuery$} from "graphqlsupport";
import {SortOrder} from "placement/domains";
import {
    Column,
    EnrichedTable,
    LinkToNewTabKeepContext,
    LoadingSpinner,
    RelayConnection,
    SimpleDropdown,
    SortBy,
    ErrorDisplay
} from "common";
import {
    AuthorizationInfo, OfferedHoursStatus,
} from "../domain";
import React, {Dispatch, useEffect, useState} from "react";
import {RelayPagination, RelayPaginationState, useRelayPagination} from "providerapp/pagination";
import {
    asQueryParam,
    ClientOfferAtRiskFilterValue,
    ClientOfferAtRiskPageAction,
} from "./ClientOfferAtRiskPage";
import {Pane} from "evergreen-ui";
import {useObservable} from "../../../rxsupport";
import {get} from "lodash";
import ClientOfferAtRiskFilterBadge from "./ClientOfferAtRiskFilterBadge";
import ClientOfferAtRiskFilter from "./ClientOfferAtRiskFilter";
import {NavigationSearchBar} from "../../../placement/components/NavigationSearchBar";
import { useApplicationRootUrl } from "common/component/ApplicationRootContext";
import { FSOInquiryFormModal } from "./fso-inquiry/FSOInquiryFormModal";
import { BehaviorSubject } from "rxjs";
import { FatOutlineButton } from "common/component/FatOutlineButton";
import {ExportButton} from "common/component/ExportButton";
import {useExportData} from "../export/useExportData";
import {ExportRequestModal} from "../export/ExportRequestModal";

export const CLIENT_OFFER_AT_RISK_LIST_URL = gql`
query authorizationsHasOfferAtRisk($first: Int = 20, $after: String, $params: AuthorizationsHasOfferAtRiskParamsInput!) {
    authorizationsHasOfferAtRisk(
        first: $first
        after: $after
        params: $params
    ) {
        edges {
            cursor
            node {
                    guid
                    bhpnAuthNumber
                    parentCase {
                        clientMrn
                        clientCity
                    }
                    client {
                        guid
                        fullName
                        city
                    }
                    atRisk
                    dateAtRiskPublished
                    authorizationItems {
                        guid
                        status
                        startDate
                        endDate
                        dateAtRiskPublished
                        atRiskResponseDueDate
                        staffingAddendumDueDate
                        staffingAddendumSubmittedDate
                        atRiskResponseDate
                        atRiskResponseReasons
                        offeredHoursStatus
                    }
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }

}
`

export const EXPORT_AT_RISK = gql`
    mutation ($filters: String) {
        exportAtRisk(filters: $filters) {
            guid
            status
        }
    }
`;

type ClientOfferAtRiskQueryResponse = RelayConnection<AuthorizationInfo> & { totalElements: number };

type ClientOfferAtRiskListProps = {
    pagination: RelayPaginationState;
    filter: ClientOfferAtRiskFilterValue;
    searchText: string;
    pageSize: number;
    sortBy: SortBy<AuthorizationInfo>;
    dispatch: Dispatch<ClientOfferAtRiskPageAction>;
}
export const ClientOfferAtRiskList = ({
                                          pagination,
                                          filter,
                                          sortBy,
                                          dispatch,
                                          searchText,
                                          pageSize,
                                      }: ClientOfferAtRiskListProps) => {
    const {execute, data, isRunning$, error} = useLazyQuery$<{
        first: number;
        after: string;
        params: {
            search: string | null,
            fromResponseDueDate: string | null,
            toResponseDueDate: string | null,
            sortDir: SortOrder,
            sortBy: string,
        }
    }, { authorizationsHasOfferAtRisk: ClientOfferAtRiskQueryResponse }>(
        CLIENT_OFFER_AT_RISK_LIST_URL,
        {
            ...pagination.toGraphQlParams(),
            params: {
                ...asQueryParam(filter, searchText),
                sortDir: sortBy.order,
                sortBy: sortBy.column,
            }
        }
    )
    const rootUrl = useApplicationRootUrl();

    const fsoInquiryVisible$ = new BehaviorSubject<boolean>(false);

    const {isRunning$: isExporting$, execute: exportData} = useExportData(EXPORT_AT_RISK);
    const isExporting = useObservable(isExporting$, false);
    const [exportGuid, setExportGuid] = useState<string | null>(null);
    const handleExport = async () => {
        const guid = await exportData(JSON.stringify({
            ...asQueryParam(filter, searchText),
            sortDir: sortBy.order,
            sortBy: sortBy.column,
        }));
        if (guid) {
            setExportGuid(guid);
        }
    };

    const startFetch = () => {
        execute().then(() => console.debug("Initial fetch"))
    }

    useEffect(() => {
        startFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, searchText]);

    const loading = useObservable(isRunning$, true);
    const totalAuths = data?.authorizationsHasOfferAtRisk?.totalElements ?? 0;
    const {onPaginationClick} = useRelayPagination(dispatch, totalAuths);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading client offer at risk" />;
    }

    const auths = data?.authorizationsHasOfferAtRisk.edges.map((edge) => edge.node) || [];


    return (
        <Pane className="h-full pt-5 m-5 bg-white shadow-md flex flex-col"
              minHeight="calc(100vh - 100px - 260px - 40px)">
            <Pane display='flex' justifyContent='space-between' padding={2} paddingBottom={8}>
                <div className={'text-sm pl-4 text-gray-600'}/>
                <div className={'flex items-center'}>

                    <div className="max-h-8 pr-4">
                        <FatOutlineButton color={'maroon'}
                                            text={"Submit FSO Inquiry"}
                                            height={"2.5rem"}
                                            border={false}
                                            onClick={() => fsoInquiryVisible$.next(true)}/>
                    </div>
                    <NavigationSearchBar placeholder={"Search by Number or Client Name"}
                                         searchQuery={searchText}
                                         setSearchQuery={(search) => dispatch({
                                             type: 'SET_SEARCH',
                                             search: search
                                         })}/>
                    <ClientOfferAtRiskFilter initFilterValue={filter} dispatch={dispatch}/>

                    <div className="mr-3 flex items-center">
                        <ExportButton
                            onClick={handleExport}
                            disabled={isExporting}
                            minWidth="7rem"
                        />
                        <ExportRequestModal guid={exportGuid} onClose={() => setExportGuid(null)}/>
                    </div>

                    <SimpleDropdown options={["10", "50", "100"]} selected={pageSize.toString()}
                                    minWidth={"2rem"}
                                    textColor={"gray-700"}
                                    setSelected={(newPageSize) => dispatch({
                                        type: "SET_PAGE_SIZE",
                                        pageSize: parseInt(newPageSize)
                                    })}/>
                    <RelayPagination pageInfo={data?.authorizationsHasOfferAtRisk.pageInfo}
                                     count={totalAuths}
                                     onClick={onPaginationClick}/>
                </div>
            </Pane>
            <ClientOfferAtRiskFilterBadge filter={filter} dispatch={dispatch}/>

            <EnrichedTable<AuthorizationInfo>
                sortBy={sortBy}
                firstColumn="bhpnAuthNumber"
                setSortBy={(newSortColumn: Column<AuthorizationInfo>) => {
                    // ignore sort on Auth Item's columns, causing confusion
                    if (!["bhpnAuthNumber",
                        "dateAtRiskPublished",
                        "client.fullName",
                        "parentCase.clientMrn",
                        "client.city"
                        ].includes(newSortColumn.attr)) {
                        return;
                    }

                    let newSortOrder = SortOrder.DESC;
                    if (newSortColumn.attr === sortBy.column && sortBy.order === newSortOrder) {
                        newSortOrder = SortOrder.ASC;
                    }

                    dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn.attr, order: newSortOrder}});
                }}
                styleOverride={{
                    customTableStyle: {height: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"},
                    tableContent: "overflow-auto flex flex-col flex-grow mb-4 scrollbar-always",
                    firstColStyle: "pl-4"
                }}
                columns={[
                    {name: 'Authorization', attr: 'bhpnAuthNumber', span: 4},
                    {name: "Client Name", attr: 'client.fullName', span: 6},
                    {name: "MRN", attr: 'parentCase.clientMrn', span: 4},
                    {name: "City", attr: 'client.city', span: 4},
                    {name: "Notification Date", attr: 'dateAtRiskPublished', span: 3},
                    {name: "At Risk Resp Due Date", attr: '_atRiskAuthItemsAtRiskResponseDueDate', span: 3},
                ]}
                rowsData={auths}
                keyColumn={"guid"}
                cellDisplayFunc={(auth, column) => {
                    switch (column.attr) {
                        case "_authItemsAtRiskOfferedHoursStatus":
                            return OfferedHoursStatus.At_Risk;
                        case "_atRiskAuthItemsAtRiskResponseDueDate":
                            return atRiskResponseDueDate(auth);
                        case "client.fullName":
                            return (
                                <LinkToNewTabKeepContext link={`${rootUrl}/clients/${auth.client.guid}/contacts`}>
                                    <span className={'underline text-blue-portal-link'}>
                                        {auth.client.fullName}
                                    </span>
                                </LinkToNewTabKeepContext>
                            )
                        case "bhpnAuthNumber":
                            return (
                                <LinkToNewTabKeepContext
                                    link={`${rootUrl}/clients/${auth.client.guid}/authorizations?authorizationGuid=${auth.guid}&tab=2`}>
                                    <span className={'underline text-blue-portal-link'}>
                                        {auth.bhpnAuthNumber}
                                    </span>
                                </LinkToNewTabKeepContext>
                            )
                        default:
                            return get(auth, column.attr);
                    }
                }}
                loadingFeat={{
                    loading: loading,
                    loadingDisplayElement: <LoadingSpinner spacing={20} height={"40vh"}/>
                }}

                emptyMessage={<div className='w-full flex items-center'>
                    <div className='m-auto pt-8'>
                        There are no client offers at risk
                    </div>
                </div>}
            />
            <FSOInquiryFormModal visible$={fsoInquiryVisible$}/>
        </Pane>
    )
}

const atRiskResponseDueDate = (auth: AuthorizationInfo) => {
    const dueDates = (auth.authorizationItems || [])
        .filter((it) => it.offeredHoursStatus === 'At_Risk' && !!it.atRiskResponseDueDate)
        .map((it) => it.atRiskResponseDueDate)
        .sort();
    return dueDates.length !== 0? dueDates[dueDates.length - 1] : "";
}



